import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Subject, ReplaySubject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, tap, delay } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

export interface Option {
  value: any;
  display: string;
}

@Component({
  selector: 'app-searchable-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
})
export class SearchableSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() label: string = 'Searchable Select';
  @Input() placeholder: string = 'Select an option';
  @Input() noEntriesFoundLabel: string = 'No matching options found';
  @Input() selectFormControl: FormControl = new FormControl();
  @Input('options') set setOptions(values: Option[]) {
    this._options = values;
    this.filteredOptions$.next(this._options ? this._options.slice() : []);
  }
  @Input() multiple: boolean = false;

  public searchControl: FormControl = new FormControl('');
  public filteredOptions$: ReplaySubject<Option[]> = new ReplaySubject<Option[]>(1);

  private _options: Option[] = [];
  private _onDestroy = new Subject<void>();

  @ViewChild('selectElem', { static: true }) selectElem!: MatSelect;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;
  // We keep this ViewChild declaration even if it might not resolve,
  // as the workaround logic checks it first.
  @ViewChild('searchInputWrapper', { static: false }) searchInputWrapper!: ElementRef<HTMLDivElement>;

  private mutationObserver!: MutationObserver;
  private panelScrollContainer!: HTMLElement | null;
  private searchInputHeight = 0;
  private hostSelectElement!: HTMLElement;
  private tempResolvedSearchInputWrapper: HTMLElement | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.filteredOptions$.next(this._options ? this._options.slice() : []);

    this.searchControl.valueChanges
      .pipe(
        takeUntil(this._onDestroy),
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.filterOptions(searchTerm))
      )
      .subscribe();
  }

  ngAfterViewInit() {
    if (!this.selectElem) {
        console.error('SearchableSelect: this.selectElem is NOT defined in ngAfterViewInit!');
        return;
    }

    // Focus search input logic
    this.selectElem.openedChange
      .pipe(
        delay(10),
        takeUntil(this._onDestroy)
      )
      .subscribe((opened) => {
        if (opened && this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.focus();
        }
      });

    try {
        this.hostSelectElement = (this.selectElem as any)._elementRef?.nativeElement;
        if (!this.hostSelectElement && this.selectElem && this.selectElem.id) {
            // Fallback if _elementRef didn't work or didn't yield a nativeElement
            this.hostSelectElement = document.getElementById(this.selectElem.id) as HTMLElement;
        }
    } catch (e) {
        console.error("SearchableSelect: Error accessing _elementRef or getting hostSelectElement.", e);
    }

    this.selectElem.openedChange
    .pipe(takeUntil(this._onDestroy))
    .subscribe(opened => {
        if (opened) {
            setTimeout(() => {
                if (!this.hostSelectElement) {
                    try {
                        this.hostSelectElement = (this.selectElem as any)._elementRef?.nativeElement;
                        if (!this.hostSelectElement && this.selectElem && this.selectElem.id) {
                          this.hostSelectElement = document.getElementById(this.selectElem.id) as HTMLElement;
                        }
                    } catch (e) { /* Error already logged or handled */ }
                }

                // Workaround for searchInputWrapper
                if (this.searchInputWrapper && this.searchInputWrapper.nativeElement) {
                    this.tempResolvedSearchInputWrapper = this.searchInputWrapper.nativeElement;
                } else if (this.searchInput && this.searchInput.nativeElement && this.searchInput.nativeElement.parentElement) {
                    const parent = this.searchInput.nativeElement.parentElement;
                    if (parent && parent.classList.contains('search-input-wrapper')) {
                        this.tempResolvedSearchInputWrapper = parent;
                    } else {
                        console.error('SearchableSelect Workaround: parentElement of searchInput is not the expected .search-input-wrapper.');
                    }
                } else {
                     console.error('SearchableSelect Workaround: Cannot attempt, searchInput or its parent is missing.');
                }

                if (this.hostSelectElement) {
                    this.initializePanelScrollAdjustment();
                } else {
                     console.error('SearchableSelect: hostSelectElement is definitively NOT valid. Cannot initialize scroll adjustment.');
                }
            }, 150);
        } else {
            this.destroyPanelScrollAdjustment();
        }
    });
  }

  trackByOptionValue(index: number, option: Option): any {
    return option.value; // Or a unique ID if option.value isn't always unique or is an object
  }

  public handleOptionToggle(event: MatOptionSelectionChange, toggledOptionValue: any): void {
    if (!this.multiple || !event.isUserInput) {
      // This handler is specifically for user-driven toggles in multiple selection mode.
      // For single-select mode, mat-select's default [formControl] binding is sufficient.
      return;
    }

    // event.source is the MatOption instance that was clicked.
    // event.source.selected is its NEW selection state (true if selected, false if deselected).

    // Get the current selection array from the FormControl.
    // It's important to get a mutable copy.
    let newSelectionArray: any[];
    const currentFormControlValues = this.selectFormControl.value;

    if (Array.isArray(currentFormControlValues)) {
      newSelectionArray = [...currentFormControlValues];
    } else {
      // If the current value wasn't an array (e.g., null, or if it was previously single select mode),
      // start fresh. If it was single select and had a value, that value is irrelevant now.
      newSelectionArray = [];
    }

    const indexInSelection = newSelectionArray.indexOf(toggledOptionValue);

    if (event.source.selected) {
      // The option was just selected by the user.
      if (indexInSelection === -1) { // If not already in our selection array
        newSelectionArray.push(toggledOptionValue);
      }
    } else {
      // The option was just deselected by the user.
      if (indexInSelection > -1) { // If it was in our selection array
        newSelectionArray.splice(indexInSelection, 1);
      }
    }

    // Now, newSelectionArray contains the state *we* believe is correct.
    // We will set the FormControl's value to this array.
    // This might happen right after mat-select itself tried to set the FormControl,
    // potentially with an incorrect (incomplete) array. Our `setValue` will override it.
    this.selectFormControl.setValue(newSelectionArray);

    // Your [MultiSelect DEBUG] log attached to selectFormControl.valueChanges
    // should now fire and show this corrected array.
  }

  private initializePanelScrollAdjustment(): void {
    if (!this.selectElem || !this.selectElem.id) {
        this.tempResolvedSearchInputWrapper = null; // Clear temp
        return;
    }
    const panelId = `${this.selectElem.id}-panel`;
    this.panelScrollContainer = document.getElementById(panelId);

    const currentSearchInputWrapperEl = this.tempResolvedSearchInputWrapper;

    if (!this.panelScrollContainer || !currentSearchInputWrapperEl || !this.hostSelectElement) {
        console.error(
            'SearchableSelect: Aborting initializePanelScrollAdjustment - critical elements missing.',
            { panel: !!this.panelScrollContainer, searchWrapper: !!currentSearchInputWrapperEl, host: !!this.hostSelectElement }
        );
        this.tempResolvedSearchInputWrapper = null; // Clear temp
        return;
    }

    this.searchInputHeight = currentSearchInputWrapperEl.offsetHeight;

    if (this.searchInputHeight <= 0) {
        // Attempt to force remeasure if height is 0 (e.g., element not fully rendered)
        this.cdr.detectChanges();
        this.searchInputHeight = currentSearchInputWrapperEl.offsetHeight;
        if (this.searchInputHeight <= 0) {
            console.error('SearchableSelect: searchInputHeight still 0 or less after detectChanges. Aborting observer setup.');
            this.tempResolvedSearchInputWrapper = null; // Clear temp
            return;
        }
    }

    if (this.mutationObserver) {
        this.mutationObserver.disconnect();
    }

    this.mutationObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.attributeName === 'aria-activedescendant') {
                const activeOptionId = this.hostSelectElement.getAttribute('aria-activedescendant');
                if (activeOptionId) {
                    Promise.resolve().then(() => {
                        const activeOptionElement = document.getElementById(activeOptionId);
                        if (activeOptionElement && this.panelScrollContainer && this.searchInputHeight > 0) {
                            requestAnimationFrame(() => {
                                if (this.panelScrollContainer) { // Re-check panelScrollContainer
                                    this.adjustScroll(activeOptionElement);
                                }
                            });
                        }
                    });
                }
            }
        });
    });

    try {
        this.mutationObserver.observe(this.hostSelectElement, { attributes: true });
    } catch (e) {
        console.error('SearchableSelect: Error starting MutationObserver.', e);
    }
  }

  private adjustScroll(activeOptionElement: HTMLElement): void {
    if (!this.panelScrollContainer || this.searchInputHeight <= 0) {
      return;
    }

    const optionOffsetTop = activeOptionElement.offsetTop;
    const currentScrollTop = this.panelScrollContainer.scrollTop;
    const optionVisibleTop = optionOffsetTop - currentScrollTop;
    const desiredMinVisibleTop = this.searchInputHeight;
    const buffer = 5; // A few pixels of buffer

    if (optionVisibleTop < desiredMinVisibleTop) {
      const newScrollTop = Math.max(0, optionOffsetTop - desiredMinVisibleTop - buffer);
      if (Math.abs(this.panelScrollContainer.scrollTop - newScrollTop) > 1) { // Avoid jitter if already close
          this.panelScrollContainer.scrollTop = newScrollTop;
      }
    }
  }

  private destroyPanelScrollAdjustment(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.panelScrollContainer = null;
    this.tempResolvedSearchInputWrapper = null;
  }

  public filterOptions(searchTerm: string | null): void {
    if (!this._options) {
      this.filteredOptions$.next([]);
      return;
    }
    let search = (searchTerm || '').toLowerCase();
    if (!search) {
      this.filteredOptions$.next(this._options.slice());
      return;
    }
    this.filteredOptions$.next(
      this._options.filter(
        (option) => option.display.toLowerCase().includes(search)
      )
    );
  }

  public clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.searchControl.setValue('');
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.destroyPanelScrollAdjustment();
  }
}
